import { z } from 'zod';

// ---- Prioritized Tasks Request ----

export const prioritizedTasksRequestSchema = z.object({
    subscriptionId: z.number().int().optional().nullable(),
    projectId: z.number().int().optional().nullable(),
    projectIds: z.array(z.number().int()).optional(),
    urgencyLevels: z.array(z.string()).optional(),
    isPastDue: z.boolean().optional().nullable(),
    isMilestone: z.boolean().optional().nullable(),
    minScore: z.number().int().optional().nullable(),
    maxDaysUntilDue: z.number().int().optional().nullable(),
    projectName: z.string().optional().nullable(),
    locationName: z.string().optional().nullable(),
    // ---- Dashboard filters (task-attribute + location-attribute) ----
    disciplineIds: z.array(z.number().int()).optional(),
    departmentIds: z.array(z.number().int()).optional(),
    phaseIds: z.array(z.number().int()).optional(),
    communityIds: z.array(z.number().int()).optional(),
    baselineIds: z.array(z.number().int()).optional(),
    isMilestonesOnly: z.boolean().optional(),
    brandIds: z.array(z.number().int()).optional(),
    areaIds: z.array(z.number().int()).optional(),
    regionIds: z.array(z.number().int()).optional(),
    managementCompanyIds: z.array(z.number().int()).optional(),
    locationTypeIds: z.array(z.number().int()).optional(),
    continentIds: z.array(z.number().int()).optional(),
    locationIds: z.array(z.number().int()).optional(),
    tagIds: z.array(z.number().int()).optional(),
});

// ---- Prioritized Tasks Response ----

export const prioritizedTaskSchema = z.object({
    taskId: z.number().int(),
    taskName: z.string().default(''),
    finalScore: z.number().default(0),
    permanentScore: z.number().default(0),
    dynamicScore: z.number().default(0),
    urgencyLevel: z.string().default(''),
    isPastDue: z.boolean().default(false),
    daysUntilDue: z.number().default(0),
    isInUserDepartment: z.boolean().default(false),
    isMilestone: z.boolean().default(false),
    calculatedAt: z.string().default(''),
    targetDate: z.string().nullish().transform(v => v ?? undefined),
    startDate: z.string().nullish().transform(v => v ?? undefined),
    locationName: z.string().nullish().transform(v => v ?? undefined),
    locationKey: z.string().default(''),
    locationAbbreviation: z.string().nullish().transform(v => v ?? undefined),
    departmentName: z.string().nullish().transform(v => v ?? undefined),
    statusName: z.string().nullish().transform(v => v ?? undefined),
    statusId: z.number().int().nullish().transform(v => v ?? undefined),
    coreStatusName: z.string().nullish().transform(v => v ?? undefined),
    isAssigned: z.boolean().nullish().transform(v => v ?? undefined),
    isFollowing: z.boolean().nullish().transform(v => v ?? undefined),
    isBlocking: z.boolean().nullish().transform(v => v ?? undefined),
    hasPrerequisites: z.boolean().nullish().transform(v => v ?? undefined),
    hasDependencies: z.boolean().nullish().transform(v => v ?? undefined),
    dependencyCount: z.number().int().nullish().transform(v => v ?? undefined),
});

export const prioritizedTasksResponseSchema = z.object({
    tasks: z.array(prioritizedTaskSchema),
    totalCount: z.number().int(),
    cacheStrategy: z.string().optional().nullable(),
    priorityThreshold: z.number().optional().nullable(),
    filterApplied: z.record(z.string(), z.any()).optional().nullable(),
    generatedAt: z.string().optional().nullable(),
});

// ---- Column Filters ----

export const statusOptionSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    coreStatusId: z.number().int().optional().nullable(),
});

export const columnFiltersRequestSchema = z.object({
    subscriptionId: z.string(),
    locationKeys: z.array(z.string()),
});

export const columnFiltersResponseSchema = z.object({
    statuses: z.array(statusOptionSchema),
});

// ---- Responsible Users ----

export const responsibleUserSchema = z.object({
    id: z.number().int(),
    name: z.string(),
});

export const responsibleUsersRequestSchema = z.object({
    locationKeys: z.array(z.string()),
    searchTerm: z.string(),
});

export const responsibleUsersResponseSchema = z.object({
    users: z.array(responsibleUserSchema),
});

// ---- Type exports ----

export type PrioritizedTasksRequest = z.infer<typeof prioritizedTasksRequestSchema>;
export type PrioritizedTask = z.infer<typeof prioritizedTaskSchema>;
export type PrioritizedTasksResponse = z.infer<typeof prioritizedTasksResponseSchema>;
export type StatusOption = z.infer<typeof statusOptionSchema>;
export type ColumnFiltersRequest = z.infer<typeof columnFiltersRequestSchema>;
export type ColumnFiltersResponse = z.infer<typeof columnFiltersResponseSchema>;
export type ResponsibleUser = z.infer<typeof responsibleUserSchema>;
export type ResponsibleUsersRequest = z.infer<typeof responsibleUsersRequestSchema>;
export type ResponsibleUsersResponse = z.infer<typeof responsibleUsersResponseSchema>;
